/**
 * Edição da pelada.
 *
 * Dados e configurações ficam em abas porque vão para rotas diferentes da API
 * — e porque a aba de configurações carrega a chave Pix, que só é enviada
 * quando muda. Sem isso, salvar o horário regravaria a chave a cada vez.
 *
 * A tela inteira é de administração: participante comum que cair aqui pela
 * URL vê o aviso, e a API recusaria as gravações de qualquer forma.
 */

import { WHATSAPP_EVENTS, type GroupDetail, type WhatsappDestination } from '@nossorachao/types';
import { toCents } from '@nossorachao/utils';
import { Copy, Lock, MessageCircle } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { PageHeader } from '@/components/layout';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  EmptyState,
  Field,
  Input,
  Skeleton,
  Tabs,
} from '@/components/ui';
import { ApiError } from '@/lib/api';
import {
  useGroup,
  useGroupWhatsapp,
  useGroupWhatsappAction,
  useUpdateGroup,
  useUpdateGroupSettings,
} from '@/lib/queries';

const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

type Tab = 'data' | 'settings' | 'whatsapp';

export function GroupEditPage() {
  const { groupId = '' } = useParams();
  const group = useGroup(groupId);
  const [tab, setTab] = useState<Tab>('data');

  if (group.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  const detail = group.data;

  if (!detail || detail.myRole === 'MEMBER') {
    return (
      <Card>
        <EmptyState
          icon={Lock}
          title="Só administradores editam a pelada"
          description="Peça ao dono ou a um administrador do grupo para fazer a alteração."
        />
      </Card>
    );
  }

  return (
    <>
      <PageHeader
        title="Editar pelada"
        subtitle={detail.name}
        back={{ to: `/grupos/${groupId}`, label: detail.name }}
      />

      <div className="mb-6">
        <Tabs<Tab>
          active={tab}
          onChange={setTab}
          tabs={[
            { key: 'data', label: 'Dados' },
            { key: 'settings', label: 'Configurações' },
            { key: 'whatsapp', label: 'WhatsApp' },
          ]}
        />
      </div>

      {/* `key` remonta o formulário quando o grupo recarrega após salvar. */}
      {tab === 'data' ? <DataForm key={detail.id} group={detail} /> : null}
      {tab === 'settings' ? <SettingsForm key={detail.id} group={detail} /> : null}
      {tab === 'whatsapp' ? <WhatsappPanel groupId={detail.id} /> : null}
    </>
  );
}

// =============================================================================
// Dados
// =============================================================================

function centsToInput(cents: number): string {
  return cents > 0 ? (cents / 100).toFixed(2).replace('.', ',') : '';
}

function DataForm({ group }: { group: GroupDetail }) {
  const navigate = useNavigate();
  const update = useUpdateGroup(group.id);

  const [form, setForm] = useState({
    name: group.name,
    description: group.description ?? '',
    venueName: group.venueName ?? '',
    venueAddress: group.venueAddress ?? '',
    venueMapsUrl: group.venueMapsUrl ?? '',
    startTime: group.startTime,
    endTime: group.endTime ?? '',
    maxPlayers: String(group.maxPlayers),
    playersPerTeam: String(group.playersPerTeam),
    monthlyFee: centsToInput(group.monthlyFeeCents),
    dueDayOfMonth: String(group.dueDayOfMonth),
    rules: group.rules ?? '',
  });
  const [weekday, setWeekday] = useState(group.weekday);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(false);

  function set(key: keyof typeof form, value: string) {
    setForm((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: '', _: '' }));
    setSaved(false);
  }

  const timePattern = /^([01]\d|2[0-3]):[0-5]\d$/;
  const fitsTwoTeams = Number(form.maxPlayers) >= Number(form.playersPerTeam) * 2;
  const canSubmit =
    form.name.trim().length >= 3 &&
    timePattern.test(form.startTime) &&
    (!form.endTime || timePattern.test(form.endTime)) &&
    fitsTwoTeams &&
    Number(form.dueDayOfMonth) >= 1;

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setErrors({});

    try {
      await update.mutateAsync({
        name: form.name.trim(),
        description: form.description.trim() || null,
        venueName: form.venueName.trim() || null,
        venueAddress: form.venueAddress.trim() || null,
        venueMapsUrl: form.venueMapsUrl.trim() || null,
        weekday,
        startTime: form.startTime,
        endTime: form.endTime || null,
        maxPlayers: Number(form.maxPlayers),
        playersPerTeam: Number(form.playersPerTeam),
        monthlyFeeCents: form.monthlyFee ? toCents(form.monthlyFee) : 0,
        dueDayOfMonth: Number(form.dueDayOfMonth),
        rules: form.rules.trim() || null,
      });
      setSaved(true);
    } catch (caught) {
      setErrors(
        caught instanceof ApiError && caught.details?.length
          ? caught.fieldErrors
          : { _: caught instanceof ApiError ? caught.message : 'Não foi possível salvar.' },
      );
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Card>
        <h3 className="mb-4 font-bold">Pelada</h3>
        <div className="space-y-4">
          <Field label="Nome" error={errors.name}>
            <Input
              value={form.name}
              onChange={(event) => set('name', event.target.value)}
              invalid={Boolean(errors.name)}
            />
          </Field>
          <Field label="Descrição" error={errors.description}>
            <Input
              placeholder="Ex.: Society das quartas, nível amador"
              value={form.description}
              onChange={(event) => set('description', event.target.value)}
            />
          </Field>
        </div>
      </Card>

      <Card>
        <h3 className="mb-4 font-bold">Local</h3>
        <div className="space-y-4">
          <Field label="Nome do campo" error={errors.venueName}>
            <Input
              placeholder="Ex.: Arena Society Vila Nova"
              value={form.venueName}
              onChange={(event) => set('venueName', event.target.value)}
            />
          </Field>
          <Field label="Endereço" error={errors.venueAddress}>
            <Input
              placeholder="Rua, número, bairro"
              value={form.venueAddress}
              onChange={(event) => set('venueAddress', event.target.value)}
            />
          </Field>
          <Field label="Link do Google Maps" error={errors.venueMapsUrl} hint="Opcional">
            <Input
              type="url"
              placeholder="https://maps.app.goo.gl/..."
              value={form.venueMapsUrl}
              onChange={(event) => set('venueMapsUrl', event.target.value)}
              invalid={Boolean(errors.venueMapsUrl)}
            />
          </Field>
        </div>
      </Card>

      <Card>
        <h3 className="mb-4 font-bold">Dia e horário</h3>
        <div className="space-y-4">
          <Field label="Dia da semana">
            <div className="flex gap-1">
              {WEEKDAYS.map((label, index) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => {
                    setWeekday(index);
                    setSaved(false);
                  }}
                  className={`flex-1 rounded-lg border py-2 text-[13px] font-semibold transition-colors ${
                    weekday === index
                      ? 'border-brand bg-brand text-on-brand'
                      : 'border-line bg-sunken text-ink-2 hover:border-line-strong'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Início" error={errors.startTime}>
              <Input
                type="time"
                value={form.startTime}
                onChange={(event) => set('startTime', event.target.value)}
                invalid={Boolean(errors.startTime)}
              />
            </Field>
            <Field label="Fim" error={errors.endTime} hint="Opcional">
              <Input
                type="time"
                value={form.endTime}
                onChange={(event) => set('endTime', event.target.value)}
              />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Máx. de jogadores" error={errors.maxPlayers}>
              <Input
                inputMode="numeric"
                value={form.maxPlayers}
                onChange={(event) => set('maxPlayers', event.target.value.replace(/\D/g, ''))}
                invalid={!fitsTwoTeams}
              />
            </Field>
            <Field label="Por time" error={errors.playersPerTeam}>
              <Input
                inputMode="numeric"
                value={form.playersPerTeam}
                onChange={(event) => set('playersPerTeam', event.target.value.replace(/\D/g, ''))}
              />
            </Field>
          </div>

          {!fitsTwoTeams ? (
            <p className="text-[13px] text-warn">
              O máximo precisa comportar dois times completos ({Number(form.playersPerTeam) * 2}{' '}
              jogadores).
            </p>
          ) : null}
        </div>
      </Card>

      <Card>
        <h3 className="mb-1 font-bold">Mensalidade</h3>
        <p className="mb-4 text-[13px] text-ink-3">
          O novo valor vale para as próximas mensalidades geradas. As já lançadas continuam com o
          valor antigo.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Valor" error={errors.monthlyFeeCents} hint="Vazio = não cobra">
            <Input
              inputMode="decimal"
              placeholder="80,00"
              value={form.monthlyFee}
              onChange={(event) => set('monthlyFee', event.target.value)}
            />
          </Field>
          <Field label="Dia do vencimento" error={errors.dueDayOfMonth}>
            <Input
              inputMode="numeric"
              value={form.dueDayOfMonth}
              onChange={(event) => set('dueDayOfMonth', event.target.value.replace(/\D/g, ''))}
              invalid={Boolean(errors.dueDayOfMonth)}
            />
          </Field>
        </div>
      </Card>

      <Card>
        <h3 className="mb-4 font-bold">Regras</h3>
        <Field error={errors.rules}>
          <textarea
            className="input min-h-[120px] py-2.5"
            placeholder="Ex.: Atraso de 15 min vai para o fim da fila"
            value={form.rules}
            onChange={(event) => set('rules', event.target.value)}
          />
        </Field>
      </Card>

      <SaveBar
        error={errors._}
        saved={saved}
        loading={update.isPending}
        disabled={!canSubmit}
        onCancel={() => navigate(`/grupos/${group.id}`)}
      />
    </form>
  );
}

// =============================================================================
// Configurações
// =============================================================================

function SettingsForm({ group }: { group: GroupDetail }) {
  const navigate = useNavigate();
  const update = useUpdateGroupSettings(group.id);
  const initial = group.settings;

  const [settings, setSettings] = useState({
    financialVisibleToMembers: initial.financialVisibleToMembers,
    allowGuests: initial.allowGuests,
    maxGuestsPerGame: String(initial.maxGuestsPerGame),
    allowNewMembers: initial.allowNewMembers,
    requireApproval: initial.requireApproval,
    rankingEnabled: initial.rankingEnabled,
    ratingsAffectSkill: initial.ratingsAffectSkill,
  });
  const [pixKey, setPixKey] = useState(initial.pixKey ?? '');
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  function set<K extends keyof typeof settings>(key: K, value: (typeof settings)[K]) {
    setSettings((current) => ({ ...current, [key]: value }));
    setError('');
    setSaved(false);
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError('');

    const trimmedPix = pixKey.trim();

    try {
      await update.mutateAsync({
        financialVisibleToMembers: settings.financialVisibleToMembers,
        allowGuests: settings.allowGuests,
        maxGuestsPerGame: Number(settings.maxGuestsPerGame) || 0,
        allowNewMembers: settings.allowNewMembers,
        requireApproval: settings.requireApproval,
        rankingEnabled: settings.rankingEnabled,
        ratingsAffectSkill: settings.ratingsAffectSkill,
        // Só manda a chave quando mudou: ela é recriptografada a cada gravação.
        ...(trimmedPix !== (initial.pixKey ?? '') ? { pixKey: trimmedPix || null } : {}),
      });
      setSaved(true);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Não foi possível salvar.');
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Card>
        <h3 className="mb-1 font-bold">Pix</h3>
        <p className="mb-4 text-[13px] text-ink-3">
          Chave usada para gerar o QR Code das mensalidades. Fica guardada criptografada e só
          administradores a veem.
        </p>
        <Field label="Chave Pix" hint="CPF, CNPJ, e-mail, telefone ou chave aleatória">
          <Input
            value={pixKey}
            maxLength={140}
            placeholder="Ex.: pelada@email.com"
            onChange={(event) => {
              setPixKey(event.target.value);
              setSaved(false);
            }}
          />
        </Field>
      </Card>

      <Card>
        <h3 className="mb-4 font-bold">Financeiro</h3>
        <Checkbox
          checked={settings.financialVisibleToMembers}
          onChange={(value) => set('financialVisibleToMembers', value)}
          label={
            <>
              <strong className="text-ink">Participantes veem o caixa e o relatório mensal</strong>
              <br />
              Saldo, receitas, despesas e totais de mensalidade — sem mostrar quem pagou. Desligado,
              cada um vê só as próprias mensalidades.
            </>
          }
        />
      </Card>

      <Card>
        <h3 className="mb-4 font-bold">Participantes</h3>
        <div className="space-y-4">
          <Checkbox
            checked={settings.allowNewMembers}
            onChange={(value) => set('allowNewMembers', value)}
            label="Aceitar novos participantes"
          />
          <Checkbox
            checked={settings.requireApproval}
            onChange={(value) => set('requireApproval', value)}
            label="Quem entra por link precisa de aprovação de um administrador"
          />
          <Checkbox
            checked={settings.allowGuests}
            onChange={(value) => set('allowGuests', value)}
            label="Permitir convidados avulsos nas partidas"
          />
          {settings.allowGuests ? (
            <Field label="Máx. de convidados por partida">
              <Input
                inputMode="numeric"
                className="max-w-[120px]"
                value={settings.maxGuestsPerGame}
                onChange={(event) => set('maxGuestsPerGame', event.target.value.replace(/\D/g, ''))}
              />
            </Field>
          ) : null}
        </div>
      </Card>

      <Card>
        <h3 className="mb-4 font-bold">Ranking e notas</h3>
        <div className="space-y-4">
          <Checkbox
            checked={settings.rankingEnabled}
            onChange={(value) => set('rankingEnabled', value)}
            label="Mostrar ranking do grupo"
          />
          <Checkbox
            checked={settings.ratingsAffectSkill}
            onChange={(value) => set('ratingsAffectSkill', value)}
            label="Avaliações pós-jogo atualizam a nota dos jogadores automaticamente"
          />
        </div>
      </Card>

      <SaveBar
        error={error}
        saved={saved}
        loading={update.isPending}
        onCancel={() => navigate(`/grupos/${group.id}`)}
      />
    </form>
  );
}

// =============================================================================
// WhatsApp
// =============================================================================

const DESTINATIONS: Array<{ key: WhatsappDestination; label: string }> = [
  { key: 'GROUP', label: 'Grupo' },
  { key: 'PRIVATE', label: 'Privado' },
  { key: 'BOTH', label: 'Ambos' },
  { key: 'OFF', label: 'Desligado' },
];

/**
 * Bot no grupo de WhatsApp da pelada.
 *
 * O vínculo é feito de dentro do grupo (`!nossorachao CÓDIGO`), não por esta
 * tela: só quem está no grupo consegue mandar o código lá, e é isso que impede
 * alguém de apontar a pelada para o grupo dos outros.
 */
function WhatsappPanel({ groupId }: { groupId: string }) {
  const query = useGroupWhatsapp(groupId);
  const action = useGroupWhatsappAction(groupId);
  const [copied, setCopied] = useState(false);
  const [tested, setTested] = useState(false);

  if (query.isLoading) return <Skeleton className="h-64 w-full" />;

  const data = query.data;
  if (!data) {
    return (
      <Card>
        <p className="text-sm text-bad">Não foi possível carregar a configuração do WhatsApp.</p>
      </Card>
    );
  }

  if (!data.available) {
    return (
      <Card>
        <EmptyState
          icon={MessageCircle}
          title="WhatsApp ainda não habilitado"
          description="O bot de WhatsApp não está ligado neste servidor. Fale com quem administra o NossoRachão."
        />
      </Card>
    );
  }

  const command = `!nossorachao ${data.linkCode}`;
  const error = action.error instanceof ApiError ? action.error.message : null;

  return (
    <div className="space-y-6">
      <Card>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-bold">Bot</h3>
          {data.bot.connected ? (
            <Badge tone="ok">Conectado{data.bot.number ? ` · +${data.bot.number}` : ''}</Badge>
          ) : (
            <Badge tone="warn">Desconectado</Badge>
          )}
        </div>

        {data.linked ? (
          <div className="space-y-4">
            <p className="text-sm text-ink-2">
              Vinculado ao grupo <strong className="text-ink">{data.chatName ?? 'sem nome'}</strong>
              {data.linkedAt
                ? ` desde ${new Intl.DateTimeFormat('pt-BR').format(new Date(data.linkedAt))}`
                : ''}
              .
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="secondary"
                loading={action.isPending && action.variables?.action === 'test'}
                onClick={() =>
                  action.mutate({ action: 'test' }, { onSuccess: () => setTested(true) })
                }
              >
                Enviar mensagem de teste
              </Button>
              <Button
                size="sm"
                variant="ghost"
                loading={action.isPending && action.variables?.action === 'unlink'}
                onClick={() => action.mutate({ action: 'unlink' })}
              >
                Desvincular grupo
              </Button>
            </div>
            {tested ? (
              <p className="text-[13px] text-ok">
                Teste na fila — deve chegar no grupo em alguns segundos.
              </p>
            ) : null}
          </div>
        ) : (
          <ol className="list-decimal space-y-2 pl-5 text-sm text-ink-2">
            <li>
              Adicione o número do bot
              {data.bot.number ? (
                <>
                  {' '}
                  <strong className="text-ink">+{data.bot.number}</strong>
                </>
              ) : null}{' '}
              no grupo de WhatsApp da pelada.
            </li>
            <li>
              No grupo, mande a mensagem:
              <div className="mt-2 flex items-center gap-2">
                <code className="rounded-md bg-sunken px-3 py-1.5 font-mono text-[15px] font-bold text-ink">
                  {command}
                </code>
                <Button
                  size="sm"
                  variant="ghost"
                  icon={Copy}
                  onClick={() => {
                    void navigator.clipboard.writeText(command);
                    setCopied(true);
                  }}
                >
                  {copied ? 'Copiado' : 'Copiar'}
                </Button>
              </div>
            </li>
            <li>O bot responde confirmando, e esta tela atualiza sozinha.</li>
          </ol>
        )}
      </Card>

      <Card>
        <Checkbox
          checked={data.enabled}
          onChange={(value) => action.mutate({ action: 'update', body: { enabled: value } })}
          label={
            <>
              <strong className="text-ink">Enviar avisos desta pelada pelo WhatsApp</strong>
              <br />
              Desligado, o bot fica no grupo mas não manda nada.
            </>
          }
        />
      </Card>

      <Card className="p-0">
        <div className="border-b border-line p-4">
          <h3 className="font-bold">O que vai para onde</h3>
          <p className="mt-1 text-[13px] text-ink-3">
            “Privado” manda para o WhatsApp de cada jogador, pelo celular do cadastro. Quem não
            informou celular continua recebendo só pelo app.
          </p>
        </div>
        <ul className="divide-y divide-line">
          {WHATSAPP_EVENTS.map((event) => (
            <li key={event.key} className="flex flex-wrap items-center justify-between gap-3 p-4">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold">{event.label}</p>
                <p className="text-[13px] text-ink-3">{event.description}</p>
              </div>
              <div className="flex gap-1" role="radiogroup" aria-label={event.label}>
                {DESTINATIONS.map((destination) => {
                  const selected = data.events[event.key] === destination.key;
                  return (
                    <button
                      key={destination.key}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      disabled={action.isPending}
                      onClick={() =>
                        action.mutate({
                          action: 'update',
                          body: { events: { [event.key]: destination.key } },
                        })
                      }
                      className={`rounded-lg border px-2.5 py-1.5 text-[12px] font-semibold transition-colors ${
                        selected
                          ? 'border-brand bg-brand text-on-brand'
                          : 'border-line bg-sunken text-ink-2 hover:border-line-strong'
                      }`}
                    >
                      {destination.label}
                    </button>
                  );
                })}
              </div>
            </li>
          ))}
        </ul>
      </Card>

      {error ? <p className="text-[13px] text-bad">{error}</p> : null}
    </div>
  );
}

function SaveBar({
  error,
  saved,
  loading,
  disabled = false,
  onCancel,
}: {
  error?: string;
  saved: boolean;
  loading: boolean;
  disabled?: boolean;
  onCancel: () => void;
}) {
  return (
    <div className="space-y-2">
      {error ? <p className="text-[13px] text-bad">{error}</p> : null}
      {saved ? <p className="text-[13px] text-ok">Alterações salvas.</p> : null}
      <div className="flex gap-3">
        <Button variant="secondary" full onClick={onCancel}>
          Voltar
        </Button>
        <Button type="submit" full loading={loading} disabled={disabled}>
          Salvar
        </Button>
      </div>
    </div>
  );
}
